import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "../AdminShell";
import { adminFetch } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-app-notifications.css";

/**
 * RAdminAppNotifications — /admin/app-notifications
 *
 * Push notifications + the app's marketing popup for the Umoona mobile app.
 * Backend endpoints (already live, same-origin, auth via adminFetch):
 *   GET  /api/admin/app/notifications
 *        → { device_count, history: [{title, body, sent, failed, sent_at, sent_by}] }
 *   POST /api/admin/app/notifications/send  body { title, body, url? }
 *        → { sent, failed, devices }
 *   GET  /api/admin/app/popup
 *        → { enabled, image_url, title, body, learn_more_url, learn_more_label, updated_at }
 *   POST /api/admin/app/popup  body { enabled, image_url, title, body,
 *        learn_more_url, learn_more_label } → { ok, id, enabled }
 *
 * Auth/loading/toast conventions match RAdminSettings / RAdminUsers.
 */

const DEFAULT_LEARN_MORE_LABEL = "Learn more";

const EMPTY_NOTIFICATION = { title: "", body: "", url: "" };

const EMPTY_POPUP = {
  enabled: false,
  image_url: "",
  title: "",
  body: "",
  learn_more_url: "",
  learn_more_label: DEFAULT_LEARN_MORE_LABEL,
};

/* "2 Jul 2026, 6:02 PM" — history timestamps */
const formatSentAt = (ts) => {
  if (!ts) return "—";
  return new Date(ts)
    .toLocaleString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
};

const RAdminAppNotifications = () => {
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [loading, setLoading] = useState(true);

  // Push notification section
  const [deviceCount, setDeviceCount] = useState(0);
  const [history, setHistory] = useState([]);
  const [notification, setNotification] = useState(EMPTY_NOTIFICATION);
  const [sending, setSending] = useState(false);

  // Marketing popup section
  const [popup, setPopup] = useState(EMPTY_POPUP);
  const [savingPopup, setSavingPopup] = useState(false);

  /* ---------------- data ---------------- */

  const fetchNotifications = useCallback(async () => {
    const response = await adminFetch("/api/admin/app/notifications", {
      navigate,
    });
    if (!response.ok) {
      throw new Error("Failed to load notification data");
    }
    const data = await response.json();
    setDeviceCount(data.device_count || 0);
    setHistory(Array.isArray(data.history) ? data.history : []);
  }, [navigate]);

  const fetchPopup = useCallback(async () => {
    const response = await adminFetch("/api/admin/app/popup", { navigate });
    if (!response.ok) {
      throw new Error("Failed to load popup settings");
    }
    const data = await response.json();
    setPopup({
      enabled: !!data.enabled,
      image_url: data.image_url || "",
      title: data.title || "",
      body: data.body || "",
      learn_more_url: data.learn_more_url || "",
      learn_more_label: data.learn_more_label || DEFAULT_LEARN_MORE_LABEL,
    });
  }, [navigate]);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([fetchNotifications(), fetchPopup()]);
    } catch (err) {
      if (!err.isAuthError) {
        console.error("App notifications load error:", err);
        toast.error(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchNotifications, fetchPopup, toast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /* ---------------- send push ---------------- */

  const handleSend = async () => {
    if (!notification.title.trim() || !notification.body.trim()) {
      toast.error("Please enter a title and message");
      return;
    }

    try {
      setSending(true);
      const payload = {
        title: notification.title.trim(),
        body: notification.body.trim(),
      };
      const url = notification.url.trim();
      if (url) payload.url = url;

      const response = await adminFetch("/api/admin/app/notifications/send", {
        method: "POST",
        body: JSON.stringify(payload),
        navigate,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to send notification");
      }
      const data = await response.json();
      const sent = data.sent || 0;
      const failed = data.failed || 0;
      toast.success(`Sent to ${sent} devices (${failed} failed)`, 4000);
      setNotification(EMPTY_NOTIFICATION);
      // Refresh device count + history to reflect the send just made.
      try {
        await fetchNotifications();
      } catch (err) {
        if (err.isAuthError) return;
      }
    } catch (err) {
      if (!err.isAuthError) toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  /* ---------------- save popup ---------------- */

  const handleSavePopup = async () => {
    try {
      setSavingPopup(true);
      const payload = {
        enabled: popup.enabled,
        image_url: popup.image_url.trim(),
        title: popup.title.trim(),
        body: popup.body.trim(),
        learn_more_url: popup.learn_more_url.trim(),
        learn_more_label:
          popup.learn_more_label.trim() || DEFAULT_LEARN_MORE_LABEL,
      };
      const response = await adminFetch("/api/admin/app/popup", {
        method: "POST",
        body: JSON.stringify(payload),
        navigate,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to save popup");
      }
      toast.success("Marketing popup saved", 3000);
    } catch (err) {
      if (!err.isAuthError) toast.error(err.message);
    } finally {
      setSavingPopup(false);
    }
  };

  const setPopupField = (field, value) =>
    setPopup((prev) => ({ ...prev, [field]: value }));

  /* ---------------- render ---------------- */

  const previewLabel =
    popup.learn_more_label.trim() || DEFAULT_LEARN_MORE_LABEL;

  return (
    <AdminShell
      active="app-notifications"
      pageClass="pg-app-notifications"
      eyebrow="App"
      title="App Notifications"
    >
      {loading ? (
        <div className="adm-loading">
          <div className="adm-spinner" />
          Loading app notifications…
        </div>
      ) : (
        <div className="apn-wrap">
          {/* ============ Send Push Notification ============ */}
          <section className="adm-card apn-card">
            <div className="adm-card-head">
              <div className="adm-card-title adm-card-title--lg">
                Send Push Notification
              </div>
              <span className="apn-devicecount">
                <span className="apn-devicecount-dot" aria-hidden="true" />
                {deviceCount.toLocaleString("en-AU")} registered{" "}
                {deviceCount === 1 ? "device" : "devices"}
              </span>
            </div>
            <p className="adm-card-text apn-intro">
              Send a push notification to everyone with the Umoona app
              installed. Keep it short — the title and message appear on the
              lock screen.
            </p>

            <label className="adm-field">
              <span className="adm-label">Title</span>
              <input
                className="adm-input"
                value={notification.title}
                onChange={(e) =>
                  setNotification((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                placeholder="e.g. New opal arrivals in store"
                maxLength={120}
              />
            </label>

            <label className="adm-field">
              <span className="adm-label">Message</span>
              <textarea
                className="adm-textarea"
                value={notification.body}
                onChange={(e) =>
                  setNotification((prev) => ({
                    ...prev,
                    body: e.target.value,
                  }))
                }
                placeholder="Write the message app users will see…"
                rows={3}
              />
            </label>

            <label className="adm-field">
              <span className="adm-label">Link URL (optional)</span>
              <input
                className="adm-input"
                type="url"
                value={notification.url}
                onChange={(e) =>
                  setNotification((prev) => ({
                    ...prev,
                    url: e.target.value,
                  }))
                }
                placeholder="https://umoonaopalmine.com.au/online-store"
              />
              <span className="apn-hint">
                Where the app opens when a user taps the notification.
              </span>
            </label>

            <div className="adm-right">
              <button
                type="button"
                className="adm-btn"
                onClick={handleSend}
                disabled={sending}
              >
                {sending ? "Sending…" : "Send to all app users"}
              </button>
            </div>

            {/* recent history */}
            <div className="apn-history">
              <div className="adm-sec-eyebrow">Recent notifications</div>
              {history.length === 0 ? (
                <div className="apn-history-empty">
                  No notifications sent yet.
                </div>
              ) : (
                <ul className="apn-history-list">
                  {history.map((item, index) => (
                    <li
                      key={index}
                      className="apn-history-item"
                    >
                      <div className="apn-history-main">
                        <div className="apn-history-title">
                          {item.title || "(no title)"}
                        </div>
                        <div className="apn-history-meta">
                          {formatSentAt(item.sent_at)}
                          {item.sent_by ? ` · by ${item.sent_by}` : ""}
                        </div>
                      </div>
                      <div className="apn-history-stats">
                        <span className="adm-chip adm-chip--teal">
                          {(item.sent || 0).toLocaleString("en-AU")} sent
                        </span>
                        {item.failed > 0 && (
                          <span className="adm-chip adm-chip--red">
                            {item.failed.toLocaleString("en-AU")} failed
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* ============ Marketing Popup ============ */}
          <section className="adm-card apn-card">
            <div className="adm-card-head">
              <div className="adm-card-title adm-card-title--lg">
                Marketing Popup
              </div>
              <label className="adm-switch">
                <input
                  type="checkbox"
                  checked={popup.enabled}
                  onChange={(e) => setPopupField("enabled", e.target.checked)}
                  aria-label="Enable marketing popup"
                />
                <span className="adm-switch-track" aria-hidden="true" />
                <span>{popup.enabled ? "Enabled" : "Disabled"}</span>
              </label>
            </div>
            <p className="adm-card-text apn-intro">
              This popup shows to app users when they open the app — use it for
              marketing, promotions and events. Turn it off when there's nothing
              to promote.
            </p>

            <div className="apn-popup-grid">
              <div className="apn-popup-form">
                <label className="adm-field">
                  <span className="adm-label">Feature graphic URL</span>
                  <input
                    className="adm-input"
                    type="url"
                    value={popup.image_url}
                    onChange={(e) => setPopupField("image_url", e.target.value)}
                    placeholder="https://…/feature-graphic.jpg"
                  />
                  <span className="apn-hint">
                    Shown across the top of the popup. Use a wide (landscape)
                    image.
                  </span>
                </label>

                {popup.image_url.trim() && (
                  <div className="apn-imgpreview">
                    <img
                      src={popup.image_url}
                      alt="Feature graphic preview"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                      onLoad={(e) => {
                        e.currentTarget.style.display = "block";
                      }}
                    />
                  </div>
                )}

                <label className="adm-field">
                  <span className="adm-label">Title</span>
                  <input
                    className="adm-input"
                    value={popup.title}
                    onChange={(e) => setPopupField("title", e.target.value)}
                    placeholder="e.g. School holiday tours now open"
                    maxLength={120}
                  />
                </label>

                <label className="adm-field">
                  <span className="adm-label">Body</span>
                  <textarea
                    className="adm-textarea"
                    value={popup.body}
                    onChange={(e) => setPopupField("body", e.target.value)}
                    placeholder="A short message shown under the title…"
                    rows={3}
                  />
                </label>

                <div className="adm-grid2">
                  <label className="adm-field">
                    <span className="adm-label">Learn More URL</span>
                    <input
                      className="adm-input"
                      type="url"
                      value={popup.learn_more_url}
                      onChange={(e) =>
                        setPopupField("learn_more_url", e.target.value)
                      }
                      placeholder="https://umoonaopalmine.com.au/…"
                    />
                  </label>
                  <label className="adm-field">
                    <span className="adm-label">Learn More button label</span>
                    <input
                      className="adm-input"
                      value={popup.learn_more_label}
                      onChange={(e) =>
                        setPopupField("learn_more_label", e.target.value)
                      }
                      placeholder={DEFAULT_LEARN_MORE_LABEL}
                    />
                  </label>
                </div>

                <div className="adm-right">
                  <button
                    type="button"
                    className="adm-btn"
                    onClick={handleSavePopup}
                    disabled={savingPopup}
                  >
                    {savingPopup ? "Saving…" : "Save popup"}
                  </button>
                </div>
              </div>

              {/* live preview */}
              <div className="apn-preview-col">
                <div className="adm-sec-eyebrow">Preview</div>
                <div className="apn-preview">
                  <div
                    className={
                      "apn-preview-card" +
                      (popup.enabled ? "" : " apn-preview-card--off")
                    }
                  >
                    {popup.image_url.trim() ? (
                      <img
                        className="apn-preview-graphic"
                        src={popup.image_url}
                        alt="Popup graphic"
                        onError={(e) => {
                          e.currentTarget.style.visibility = "hidden";
                        }}
                        onLoad={(e) => {
                          e.currentTarget.style.visibility = "visible";
                        }}
                      />
                    ) : (
                      <div className="apn-preview-graphic apn-preview-graphic--empty">
                        Feature graphic
                      </div>
                    )}
                    <div className="apn-preview-body">
                      <div className="apn-preview-title">
                        {popup.title.trim() || "Popup title"}
                      </div>
                      <div className="apn-preview-text">
                        {popup.body.trim() ||
                          "Your marketing message appears here."}
                      </div>
                      <button
                        type="button"
                        className="apn-preview-btn"
                        disabled
                      >
                        {previewLabel}
                      </button>
                    </div>
                  </div>
                  <div className="apn-preview-note">
                    {popup.enabled
                      ? "Live — app users see this on open."
                      : "Disabled — not shown to app users."}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </AdminShell>
  );
};

export default RAdminAppNotifications;
